import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, ArrowLeft, Save, Shield, BarChart3, Trash2, BookOpen, FileText, Lock, CalendarDays } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import logoUrl from "@/assets/waiterix-logo.png";
import type { Restaurant } from "@/shared/schema";
import { TableQRCodes } from "@/components/TableQRCodes";
import { PaymentGatewayOnboarding } from "@/components/PaymentGatewayOnboarding";
import { SubscriptionStatus } from "@/components/SubscriptionStatus";
import { RestaurantInfoForm } from "@/components/RestaurantInfoForm";
import { FAQManagementPanel } from "@/components/FAQManagementPanel";
import { AnalyticsTab } from "@/components/AnalyticsTab";
import { FinancialReportsTab } from "@/components/FinancialReportsTab";
import { ReservationsTab } from "@/components/ReservationsTab";
import { AdminReceptionistSettings } from "@/components/AdminReceptionistSettings";
import { OperatingHoursSettings } from "@/components/OperatingHoursSettings";
import { signOut } from "@/lib/firebase";

export default function AdminPage() {
  const [, setLocation] = useLocation();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("settings");
  const [activeSubTab, setActiveSubTab] = useState("profile");

  // Admin verification state
  const [adminVerified, setAdminVerified] = useState(false);
  const [verificationPassword, setVerificationPassword] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [checkingPassword, setCheckingPassword] = useState(true);

  // Settings state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [aiWaiterEnabled, setAiWaiterEnabled] = useState(true);
  const [autoPrintOrders, setAutoPrintOrders] = useState(false);
  const [printerIp, setPrinterIp] = useState(localStorage.getItem('waiterix_printer_ip') || '192.168.1.100');
  const [printerPort, setPrinterPort] = useState(localStorage.getItem('waiterix_printer_port') || '9100');


  // Analytics state
  const [analyticsRange, setAnalyticsRange] = useState<"week" | "month" | "lifetime">("month");

  // Fetch restaurant data
  const { data: restaurant, isLoading: isRestaurantLoading, error: restaurantError } = useQuery<Restaurant>({
    queryKey: ['/api/restaurant'],
    enabled: !!user,
  });

  // Fetch analytics data
  const { data: analyticsData, isLoading: isAnalyticsLoading } = useQuery<{
    period: string;
    tableScans: { totalScans: number; scansByTable: { tableNumber: string; count: number }[] };
    aiCalls: { totalCalls: number; callsByType: { callType: string; count: number }[] };
  }>({
    queryKey: ['/api/analytics/stats', analyticsRange],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/analytics/stats?period=${analyticsRange}`);
      if (!res.ok) throw new Error('Failed to fetch analytics');
      return res.json();
    },
    enabled: !!user && !!restaurant,
  });

  // Set password mutation
  const setPasswordMutation = useMutation({
    mutationFn: async (data: { password: string; currentPassword?: string }) => {
      const response = await apiRequest('POST', '/api/admin/password/set', data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Admin password updated successfully",
      });
      setNewPassword("");
      setConfirmPassword("");
      setCurrentPassword("");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle AI waiter mutation
  const toggleAiWaiterMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiRequest('PUT', '/api/admin/settings/ai-waiter', { enabled });
      return response.json();
    },
    onSuccess: (_data, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['/api/restaurant'] });
      toast({
        title: "Success",
        description: `AI Waiter ${enabled ? "enabled" : "disabled"} successfully`,
      });
    },
    onError: (error: Error, enabled) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      // Revert the switch back to the opposite of what we tried to set
      setAiWaiterEnabled(!enabled);
    },
  });

  // Toggle auto-print mutation
  const toggleAutoPrintMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiRequest('PUT', '/api/admin/settings/auto-print', { enabled });
      return response.json();
    },
    onSuccess: (_data, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['/api/restaurant'] });
      toast({
        title: "Success",
        description: `Auto-print ${enabled ? "enabled" : "disabled"} successfully`,
      });
    },
    onError: (error: Error, enabled) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      // Revert the switch back to the opposite of what we tried to set
      setAutoPrintOrders(!enabled);
    },
  });

  // Update restaurant mutation
  const updateRestaurantMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest('PUT', `/api/restaurant/${restaurant?.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/restaurant'] });
      toast({
        title: "Success",
        description: "Restaurant information updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      await apiRequest('DELETE', '/api/auth/delete-account');
    },
    onSuccess: async () => {
      toast({
        title: "Account Deleted",
        description: "Your account has been permanently deleted",
      });
      try {
        await signOut();
      } catch (error) {
        console.error("Error signing out:", error);
      }
      window.location.href = '/';
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update AI waiter and auto-print state when restaurant data loads
  useEffect(() => {
    if (restaurant) {
      setAiWaiterEnabled(restaurant.aiWaiterEnabled ?? true);
      setAutoPrintOrders(restaurant.autoPrintOrders ?? false);
    }
  }, [restaurant]);

  // Check if admin password is required when page loads
  useEffect(() => {
    const checkAdminPassword = async () => {
      if (!user) return;

      try {
        const response = await apiRequest('POST', '/api/admin/password/check');
        const { hasPassword } = await response.json();

        if (!hasPassword) {
          // No password set, allow access
          setAdminVerified(true);
        }
        // If password is set, user must verify
      } catch (error) {
        console.error('Error checking admin password:', error);
        // On error, require verification to be safe
      } finally {
        setCheckingPassword(false);
      }
    };

    checkAdminPassword();
  }, [user]);

  // Handle admin verification
  const handleAdminVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationPassword) {
      toast({
        title: "Error",
        description: "Please enter your admin passcode",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      const response = await apiRequest('POST', '/api/admin/password/verify', {
        password: verificationPassword,
      });
      const { valid } = await response.json();

      if (valid) {
        setAdminVerified(true);
        setVerificationPassword("");
      } else {
        toast({
          title: "Invalid Passcode",
          description: "The admin passcode you entered is incorrect",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Admin verification error:', error);
      toast({
        title: "Error",
        description: "Failed to verify admin passcode",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle password form submit
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword) {
      toast({
        title: "Error",
        description: "Current passcode is required",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passcodes do not match",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length !== 6 || !/^\d{6}$/.test(newPassword)) {
      toast({
        title: "Error",
        description: "New passcode must be exactly 6 digits",
        variant: "destructive",
      });
      return;
    }

    setPasswordMutation.mutate({
      password: newPassword,
      currentPassword: currentPassword,
    });
  };

  // Handle AI waiter toggle
  const handleAiWaiterToggle = (enabled: boolean) => {
    setAiWaiterEnabled(enabled);
    toggleAiWaiterMutation.mutate(enabled);
  };

  // Handle auto-print toggle
  const handleAutoPrintToggle = (enabled: boolean) => {
    setAutoPrintOrders(enabled);
    toggleAutoPrintMutation.mutate(enabled);
  };

  // Handle unauthorized errors
  if (restaurantError && isUnauthorizedError(restaurantError as Error)) {
    setLocation('/');
    return null;
  }

  // Loading state
  if (isAuthLoading || isRestaurantLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" data-testid="loader-admin" />
      </div>
    );
  }

  // If not authenticated, redirect
  if (!user || !restaurant) {
    setLocation('/');
    return null;
  }

  // Loading state for admin password check
  if (checkingPassword) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" data-testid="loader-admin-check" />
      </div>
    );
  }

  // Admin verification required
  if (!adminVerified) {
    return (
      <div className="flex flex-col min-h-screen">
        <header className="border-b p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setLocation('/dashboard')}
                data-testid="button-back-dashboard"
                title="Back to Dashboard"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <img src={logoUrl} alt="Waiterix" className="h-10 hover-lift smooth-transition" />
              <div>
                <h1 className="text-xl font-bold">Admin Settings</h1>
                <p className="text-sm text-muted-foreground">{restaurant.name}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="h-8 w-8 text-primary" />
              <div>
                <h2 className="text-xl font-semibold">Admin Verification Required</h2>
                <p className="text-sm text-muted-foreground">
                  Enter your admin passcode to access settings
                </p>
              </div>
            </div>

            <form onSubmit={handleAdminVerification} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-verification-password">Admin Passcode</Label>
                <Input
                  id="admin-verification-password"
                  type="password"
                  inputMode="numeric"
                  placeholder="Enter 6-digit passcode"
                  value={verificationPassword}
                  onChange={(e) => setVerificationPassword(e.target.value)}
                  maxLength={6}
                  autoFocus
                  data-testid="input-admin-verification"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation('/dashboard')}
                  className="flex-1"
                  data-testid="button-cancel-verification"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isVerifying || !verificationPassword}
                  className="flex-1"
                  data-testid="button-verify-admin"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Verify
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <header className="border-b p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocation('/dashboard')}
              data-testid="button-back-dashboard"
              title="Back to Dashboard"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <img src={logoUrl} alt="Waiterix" className="h-10 hover-lift smooth-transition" data-testid="img-waiterix-logo" />
            <div>
              <h1 className="text-xl font-bold">Admin Settings</h1>
              <p className="text-sm text-muted-foreground">{restaurant.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6">
        <div className="max-w-5xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full max-w-5xl grid-cols-5" data-testid="tabs-admin">
              <TabsTrigger value="settings" data-testid="tab-settings">
                <Shield className="h-4 w-4 mr-2" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="knowledge" data-testid="tab-knowledge">
                <BookOpen className="h-4 w-4 mr-2" />
                Knowledge
              </TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports">
                <FileText className="h-4 w-4 mr-2" />
                Reports
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics">
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="reservation" data-testid="tab-reservation">
                <CalendarDays className="h-4 w-4 mr-2" />
                Reservation
              </TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="space-y-6">
              <Tabs value={activeSubTab || "profile"} onValueChange={setActiveSubTab} className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-6" data-testid="tabs-admin-settings">
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  <TabsTrigger value="subscription">Subscription</TabsTrigger>
                  <TabsTrigger value="ai">AI Management</TabsTrigger>
                  <TabsTrigger value="printer">Printer</TabsTrigger>
                  <TabsTrigger value="security">Security</TabsTrigger>
                </TabsList>

                {/* 1. Subscription Tab */}
                <TabsContent value="subscription" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <PaymentGatewayOnboarding restaurant={restaurant} />
                    <SubscriptionStatus restaurant={restaurant} />
                  </div>
                </TabsContent>

                {/* 2. Profile Settings Tab */}
                <TabsContent value="profile" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold">Restaurant Information</h3>
                      <RestaurantInfoForm
                        restaurantId={restaurant.id}
                        initialData={{
                          name: restaurant.name,
                          description: restaurant.description || '',
                          address: restaurant.address || '',
                          city: restaurant.city || '',
                          state: restaurant.state || '',
                          country: restaurant.country || '',
                          phone: restaurant.phone || '',
                          coverImageUrl: restaurant.coverImageUrl || '',
                          defaultLanguage: restaurant.defaultLanguage || 'en',
                          timezone: restaurant.timezone || '',
                        }}
                        onSubmit={(data) => updateRestaurantMutation.mutate(data)}
                        showSecurityFields={false}
                      />
                      <OperatingHoursSettings restaurantId={restaurant.id} />
                    </div>
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold">Table QR Codes</h3>
                      <Card className="p-6">
                        <TableQRCodes
                          restaurantId={restaurant.id}
                          restaurantName={restaurant.name}
                        />
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                {/* 3. Security Settings Tab */}
                <TabsContent value="security" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="max-w-2xl space-y-6">
                    <Card className="p-6">
                      <h2 className="text-lg font-semibold mb-4">Change Admin Passcode</h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Change the passcode required to access admin settings and analytics.
                      </p>
                      <form onSubmit={handlePasswordSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="current-password">Current Passcode *</Label>
                          <Input
                            id="current-password"
                            type="password"
                            inputMode="numeric"
                            placeholder="Enter current passcode"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            data-testid="input-current-password"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="new-password">New Passcode (6 digits) *</Label>
                          <Input
                            id="new-password"
                            type="password"
                            inputMode="numeric"
                            placeholder="Enter new passcode"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            data-testid="input-new-password"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="confirm-password">Confirm New Passcode *</Label>
                          <Input
                            id="confirm-password"
                            type="password"
                            inputMode="numeric"
                            placeholder="Confirm new passcode"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            data-testid="input-confirm-password"
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={setPasswordMutation.isPending || !newPassword || !confirmPassword || !currentPassword}
                          data-testid="button-save-password"
                        >
                          {setPasswordMutation.isPending ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Change Passcode
                            </>
                          )}
                        </Button>
                      </form>
                    </Card>

                    <Card className="border-destructive/50">
                      <div className="p-6 space-y-4">
                        <div>
                          <h3 className="text-lg font-semibold text-destructive">Danger Zone</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            Irreversible action that will permanently delete your data.
                          </p>
                        </div>

                        <div className="space-y-3 pt-3 border-t">
                          <div>
                            <h4 className="font-medium mb-1">Delete Account</h4>
                            <p className="text-sm text-muted-foreground mb-3">
                              Permanently delete your entire account, including all restaurants, menu items, orders, and personal data. This cannot be undone.
                            </p>
                            <Button
                              variant="destructive"
                              onClick={() => {
                                const confirmed = confirm('⚠️ FINAL WARNING ⚠️\n\nThis will permanently delete your ENTIRE ACCOUNT including:\n- All restaurants\n- All menu items\n- All orders and customer data\n- All personal information\n\nThis action is IRREVERSIBLE and cannot be undone.\n\nAre you absolutely certain you want to proceed?');

                                if (confirmed) {
                                  const doubleCheck = confirm('Type DELETE in the next prompt to confirm account deletion');

                                  if (doubleCheck) {
                                    const typed = prompt('Type DELETE (in capital letters) to confirm:');
                                    if (typed === 'DELETE') {
                                      deleteAccountMutation.mutate();
                                    } else {
                                      toast({
                                        title: "Cancelled",
                                        description: "Account deletion cancelled.",
                                      });
                                    }
                                  }
                                }
                              }}
                              data-testid="button-delete-account"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Account Permanently
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </TabsContent>

                {/* 4. AI Usage Tab */}
                <TabsContent value="ai" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="max-w-3xl space-y-6">
                    <Card className="p-6">
                      <h2 className="text-lg font-semibold mb-4">AI Waiter</h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Enable or disable the AI Waiter feature for your restaurant's customer menu.
                      </p>
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <p className="font-medium">AI Waiter Status</p>
                          <p className="text-sm text-muted-foreground">
                            {aiWaiterEnabled ? "AI assistance is active" : "AI assistance is disabled"}
                          </p>
                        </div>
                        <Switch
                          checked={aiWaiterEnabled}
                          onCheckedChange={handleAiWaiterToggle}
                          disabled={toggleAiWaiterMutation.isPending}
                          data-testid="switch-ai-waiter"
                        />
                      </div>

                      <div className="pt-6 border-t mt-6">
                        <h3 className="text-md font-semibold mb-4">Phone Receptionist Setup</h3>
                        <AdminReceptionistSettings restaurantId={restaurant.id} />
                      </div>
                    </Card>
                  </div>
                </TabsContent>

                {/* 5. Printer Settings Tab */}
                <TabsContent value="printer" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="max-w-2xl space-y-6">
                    <Card className="p-6">
                      <h2 className="text-lg font-semibold mb-4">Auto-Print Orders</h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Automatically print new orders as they arrive. For silent printing without a dialog, ensure your browser is running in Kiosk mode with the --kiosk-printing flag.
                      </p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Auto-Print Status</p>
                          <p className="text-sm text-muted-foreground">
                            {autoPrintOrders ? "Orders will print automatically" : "Manual printing only"}
                          </p>
                        </div>
                        <Switch
                          checked={autoPrintOrders}
                          onCheckedChange={handleAutoPrintToggle}
                          disabled={toggleAutoPrintMutation.isPending}
                          data-testid="switch-auto-print"
                        />
                      </div>

                      <div className="pt-4 mt-4 border-t">
                        <h3 className="text-md font-semibold mb-2">Thermal Printer Settings</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Configure your thermal printer's local IP address and port for silent printing.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="printer-ip">Printer IP Address</Label>
                            <Input
                              id="printer-ip"
                              value={printerIp}
                              onChange={(e) => { setPrinterIp(e.target.value); localStorage.setItem('waiterix_printer_ip', e.target.value); }}
                              placeholder="192.168.1.100"
                              data-testid="input-printer-ip"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="printer-port">Printer Port</Label>
                            <Input
                              id="printer-port"
                              value={printerPort}
                              onChange={(e) => { setPrinterPort(e.target.value); localStorage.setItem('waiterix_printer_port', e.target.value); }}
                              placeholder="9100"
                              data-testid="input-printer-port"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          These settings are stored locally on this device.
                        </p>
                      </div>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </TabsContent>

            <TabsContent value="knowledge" className="space-y-6">
              <FAQManagementPanel restaurantId={restaurant.id} />
            </TabsContent>

            <TabsContent value="reports" className="space-y-6">
              <FinancialReportsTab
                restaurantId={restaurant.id}
                restaurantName={restaurant.name}
              />
            </TabsContent>

            <TabsContent value="analytics" className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
                <div className="flex gap-2">
                  <Button
                    variant={analyticsRange === "week" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAnalyticsRange("week")}
                    data-testid="button-range-week"
                  >
                    This Week
                  </Button>
                  <Button
                    variant={analyticsRange === "month" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAnalyticsRange("month")}
                    data-testid="button-range-month"
                  >
                    This Month
                  </Button>
                  <Button
                    variant={analyticsRange === "lifetime" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAnalyticsRange("lifetime")}
                    data-testid="button-range-lifetime"
                  >
                    Lifetime
                  </Button>
                </div>
              </div>

              {isAnalyticsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" data-testid="loader-analytics" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold mb-4">Table Scans</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-3xl font-bold" data-testid="text-total-scans">
                            {analyticsData?.tableScans.totalScans || 0}
                          </p>
                          <p className="text-sm text-muted-foreground">Total QR scans</p>
                        </div>
                        {analyticsData?.tableScans.scansByTable && analyticsData.tableScans.scansByTable.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium">By Table:</p>
                            {analyticsData.tableScans.scansByTable.map((item) => (
                              <div key={item.tableNumber} className="flex justify-between items-center">
                                <span className="text-sm" data-testid={`text-table-${item.tableNumber}`}>
                                  Table {item.tableNumber}
                                </span>
                                <span className="text-sm font-medium" data-testid={`text-table-${item.tableNumber}-scans`}>
                                  {item.count} scans
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>

                    <Card className="p-6">
                      <h3 className="text-lg font-semibold mb-4">API Usage</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-3xl font-bold" data-testid="text-total-api-calls">
                            {analyticsData?.aiCalls.totalCalls || 0}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {analyticsRange === "week" ? "AI calls this week" :
                              analyticsRange === "month" ? "AI calls this month" :
                                "Lifetime AI API calls"}
                          </p>
                        </div>
                        {analyticsData?.aiCalls.callsByType && analyticsData.aiCalls.callsByType.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium">By Type:</p>
                            {analyticsData.aiCalls.callsByType.map((item) => (
                              <div key={item.callType} className="flex justify-between items-center">
                                <span className="text-sm capitalize" data-testid={`text-api-${item.callType}`}>
                                  {item.callType.replace(/_/g, ' ')}
                                </span>
                                <span className="text-sm font-medium" data-testid={`text-api-${item.callType}-count`}>
                                  {item.count} calls
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>

                  {(!analyticsData?.tableScans.totalScans && !analyticsData?.aiCalls.totalCalls) && (
                    <Card className="p-12">
                      <div className="text-center">
                        <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-lg font-medium">No analytics data yet</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Data will appear here once customers start using your menu
                        </p>
                      </div>
                    </Card>
                  )}
                </>
              )}

              {/* Customer Ratings Section */}
              <div className="mt-8">
                <h2 className="text-2xl font-bold mb-4">Customer Ratings & Feedback</h2>
                {restaurant && <AnalyticsTab restaurantId={restaurant.id} />}
              </div>
            </TabsContent>

            <TabsContent value="reservation" className="space-y-6">
              <ReservationsTab restaurantId={restaurant.id} mode="admin" />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
